import { Component, computed, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { SignupStore } from '../../domain/signup.store';

/**
 * Step one: which plan, how many seats, and which Business Starter to begin from.
 *
 * The catalogue comes from the live `GET /plans` rather than from a constant, which is the point of
 * the gateway forwarding that read at all: a plan added or repriced in Admin appears here without a
 * frontend release, and the price the customer sees is the price the subscription is created at.
 *
 * The price is `plan.price` — the formatted string off the model — never `amountMinor`. That integer
 * is the currency's minor unit, so rendering it raw would show "4900" for forty-nine euros, and
 * dividing it here would be a second place that money arithmetic happens. `formatMinor` in
 * `@processpuzzle/platform-admin` is the one place.
 *
 * Seats are checked against nothing on this side. Admin owns the plan's `USERS` limit and answers
 * 400 above it; a copy of the limit here would be a copy that drifts, and the one that drifted would
 * reject a band that had just been widened.
 *
 * The starter comes from the published catalog, and "an empty application" is its first option and
 * the default rather than the absence of a choice: it is a real way to start, and it is what the seed
 * did before the choice existed. It travels as an absent `starterId`, so the empty option's value is
 * the empty string and {@link push} drops it.
 */
@Component({
  selector: 'pp-plan-step',
  imports: [ReactiveFormsModule, MatCard, MatCardTitle, MatCardContent, MatFormField, MatLabel, MatError, MatInput, MatSelect, MatOption, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.signup.plan'">
      <p class="step-lead">{{ t('lead') }}</p>

      @if (store.plans().length === 0) {
        <p test-id="plan-step-empty">{{ t('empty') }}</p>
      }

      <div class="plan-cards">
        @for (plan of store.plans(); track plan.code) {
          <mat-card
            class="plan-card"
            [class.selected]="form.controls.planCode.value === plan.code"
            [attr.test-id]="'plan-card-' + plan.code"
            tabindex="0"
            role="radio"
            [attr.aria-checked]="form.controls.planCode.value === plan.code"
            (click)="select(plan.code)"
            (keydown.enter)="select(plan.code)"
            (keydown.space)="select(plan.code)"
          >
            <mat-card-title>{{ plan.name }}</mat-card-title>
            <mat-card-content>
              <p class="plan-price">{{ plan.price }} / {{ t('interval.' + plan.interval) }}</p>
              @if (plan.description) {
                <p class="plan-description">{{ plan.description }}</p>
              }
              @for (limit of plan.limits; track limit.metric) {
                <p class="plan-limit">{{ t('limit', { metric: t('metric.' + limit.metric), quantity: limit.maxQuantity }) }}</p>
              }
            </mat-card-content>
          </mat-card>
        }
      </div>

      <form [formGroup]="form" class="plan-step-form">
        <mat-form-field>
          <mat-label>{{ t('seats') }}</mat-label>
          <input id="signup-quantity" matInput type="number" min="1" formControlName="quantity" />
          @if (form.controls.quantity.hasError('required') || form.controls.quantity.hasError('min')) {
            <mat-error>{{ t('seatsRequired') }}</mat-error>
          }
        </mat-form-field>
        <p class="step-hint">{{ t('seatsHint') }}</p>

        <mat-form-field>
          <mat-label>{{ t('starter') }}</mat-label>
          <mat-select id="signup-starter" formControlName="starterId">
            <mat-option value="">{{ t('starterEmpty') }}</mat-option>
            @for (starter of store.starters(); track starter.id) {
              <mat-option [value]="starter.id">{{ starter.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <p class="step-hint" test-id="plan-step-starter-hint">{{ selectedStarter()?.description || t('starterHint') }}</p>
      </form>

      @if (form.controls.planCode.touched && form.controls.planCode.hasError('required')) {
        <p class="step-error" test-id="plan-step-no-plan">{{ t('planRequired') }}</p>
      }
    </ng-container>
  `,
  styles: [
    `
      .plan-cards {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 16px;
      }

      .plan-card {
        cursor: pointer;
        min-width: 220px;
        border: 2px solid transparent;
      }

      .plan-card.selected {
        border-color: var(--mat-sys-primary, #3f51b5);
      }

      .plan-price {
        font-weight: 600;
      }

      .plan-limit,
      .plan-description,
      .step-hint {
        font-size: 0.85rem;
        opacity: 0.75;
      }

      .step-error {
        color: var(--mat-sys-error, #b3261e);
      }

      .plan-step-form {
        display: flex;
        flex-direction: column;
        max-width: 360px;
      }
    `,
  ],
})
export class PlanStepComponent implements OnInit {
  readonly store = inject(SignupStore);
  private readonly formBuilder = inject(FormBuilder);

  readonly form = this.formBuilder.nonNullable.group({
    planCode: ['', Validators.required],
    quantity: [1, [Validators.required, Validators.min(1)]],
    starterId: [''],
  });

  private readonly starterId = toSignal(this.form.controls.starterId.valueChanges, { initialValue: this.form.controls.starterId.value });

  /** The chosen starter, for its description; `undefined` for the empty application. */
  readonly selectedStarter = computed(() => this.store.starters().find((starter) => starter.id === this.starterId()));

  ngOnInit(): void {
    this.store.loadPlans();
    this.store.loadStarters();
    const subscription = this.store.draft().subscription;
    if (subscription?.planCode) this.form.patchValue({ planCode: subscription.planCode });
    if (subscription?.quantity) this.form.patchValue({ quantity: subscription.quantity });
    if (subscription?.starterId) this.form.patchValue({ starterId: subscription.starterId });
    // Written through on every change rather than on a Next click. The stepper's own navigation
    // does not pass through this component, so a step left by clicking its neighbour's header would
    // otherwise lose whatever was typed.
    this.form.valueChanges.subscribe(() => this.push());
  }

  select(planCode: string): void {
    this.form.patchValue({ planCode });
    this.form.controls.planCode.markAsTouched();
  }

  private push(): void {
    const value = this.form.getRawValue();
    this.store.updateDraft({ subscription: { planCode: value.planCode, quantity: Number(value.quantity), starterId: value.starterId || undefined } });
  }
}
